import * as Sentry from "@sentry/react";

/**
 * Wrap an async user-action in a Sentry performance span and capture exceptions.
 * Use for high-value flows: signup, payment, OAuth.
 */
export async function trackAction<T>(
  name: string,
  op: string,
  fn: () => Promise<T>,
  extras: Record<string, unknown> = {},
): Promise<T> {
  return await Sentry.startSpan(
    { name, op, attributes: extras as Record<string, string | number | boolean> },
    async (span) => {
      try {
        const result = await fn();
        span?.setStatus?.({ code: 1 }); // OK
        return result;
      } catch (error) {
        span?.setStatus?.({ code: 2, message: "error" });
        Sentry.captureException(error, { tags: { action: name } });
        throw error;
      }
    },
  );
}

export const trackSignup = <T>(fn: () => Promise<T>, extras: Record<string, unknown> = {}) =>
  trackAction("user.signup", "user.action", fn, extras).then((r) => {
    incrementMetric("user.signup.count");
    return r;
  });

export const trackPayment = <T>(fn: () => Promise<T>, extras: Record<string, unknown> = {}) =>
  trackAction("payment.checkout", "user.action", fn, extras).then((r) => {
    incrementMetric("payment.success.count");
    return r;
  });

export const trackOAuth = <T>(provider: string, fn: () => Promise<T>) =>
  trackAction(`auth.${provider}.oauth`, "user.action", fn, { provider });

/** Best-effort metric increment — newer Sentry SDKs expose metrics, gracefully no-op otherwise. */
export function incrementMetric(name: string, value = 1, tags: Record<string, string> = {}) {
  try {
    const m = (Sentry as any).metrics;
    if (m?.increment) m.increment(name, value, { tags });
  } catch {
    /* no-op */
  }
}

/** Tag every event with the active user once Clerk has loaded. */
export function setSentryUser(user: { id: string; email?: string | null; country?: string | null }) {
  Sentry.setUser({
    id: user.id,
    email: user.email ?? undefined,
    segment: user.country ?? undefined,
  });
}

export function clearSentryUser() {
  Sentry.setUser(null);
}
